/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import { services } from '../../services';

interface Contact {
  _id: string;
  consent: boolean;
  deletedAt: string | null;
  firstName: string;
  lastName: string;
  email: string;
  confirmEmail: string;
  country: string;
  businessType: string;
  comments: string;
  submittedAt: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface ContactsPageProps {}

const ContactsPage: React.FC<ContactsPageProps> = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalContacts, setTotalContacts] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching contacts from:', API_ENDPOINTS.CONTACTS.LIST);
      const response = await services.get(API_ENDPOINTS.CONTACTS.LIST);

      console.log('Contacts API Response:', response);

      if (response.data && response.data.success) {
        const contactsData = response.data.data || [];
        setContacts(Array.isArray(contactsData) ? contactsData : []);
        setTotalContacts(response.data.total || contactsData.length);
        setTotalPages(response.data.pages || 1);
        setCurrentPage(response.data.page || 1);
      } else {
        console.log('No contacts data found, using fallback');
        setContacts([]);
        setTotalContacts(0);
        setTotalPages(1);
        setCurrentPage(1);
      }
    } catch (err: any) {
      console.error('Error fetching contacts:', err);
      setError(err.response?.data?.message || 'Failed to fetch contacts');
      setContacts([]);
      setTotalContacts(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContactDetails = async (contactId: string) => {
    try {
      setContactLoading(true);
      console.log('Fetching contact details for ID:', contactId);
      const response = await services.get(API_ENDPOINTS.CONTACTS.GET_BY_ID(contactId));

      console.log('Contact Details API Response:', response);

      if (response.data && response.data.success) {
        setSelectedContact(response.data.data);
        setShowContactModal(true);
      } else {
        console.error('Failed to fetch contact details');
      }
    } catch (err: any) {
      console.error('Error fetching contact details:', err);
    } finally {
      setContactLoading(false);
    }
  };

  // Filter contacts based on search term
  const filteredContacts = contacts.filter((contact) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      contact.firstName?.toLowerCase().includes(searchLower) ||
      contact.lastName?.toLowerCase().includes(searchLower) ||
      contact.email?.toLowerCase().includes(searchLower) ||
      contact.country?.toLowerCase().includes(searchLower) ||
      contact.businessType?.toLowerCase().includes(searchLower)
    );
  });

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getStatusText = (consent: boolean) => {
    return consent ? 'Consented' : 'Not Consented';
  };

  if (loading) {
    return (
      <div
        className='flex items-center justify-center min-h-screen'
        style={{ backgroundColor: '#0B222C' }}
      >
        <div className='text-center'>
          <div
            className='animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4'
            style={{ borderColor: '#095D52' }}
          ></div>
          <p className='font-work-sans' style={{ color: '#ede8c5' }}>
            Loading contacts...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className='flex items-center justify-center min-h-screen'
        style={{ backgroundColor: '#0B222C' }}
      >
        <div className='text-center'>
          <div className='text-red-500 text-xl mb-4'>⚠️</div>
          <p className='font-work-sans text-lg mb-4' style={{ color: '#ede8c5' }}>
            {error}
          </p>
          <button
            onClick={fetchContacts}
            className='px-6 py-2 rounded-lg font-work-sans transition-all duration-300 hover:scale-105'
            style={{
              backgroundColor: '#095D52',
              border: '1px solid #095D52',
              color: '#ede8c5',
              boxShadow: '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
              backgroundImage:
                'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#bbe425';
              e.currentTarget.style.boxShadow =
                '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#095D52';
              e.currentTarget.style.boxShadow =
                '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='px-8 py-8' style={{ backgroundColor: '#0B222C', minHeight: '100vh' }}>
      {/* Header */}
      <div className='mb-10'>
        <h1
          className='text-3xl font-bold mb-2 font-dm-serif'
          style={{
            color: '#ede8c5',
            textShadow: '0 2px 6px rgba(0, 0, 0, 0.4), 0 0 10px rgba(187, 228, 37, 0.2)',
            letterSpacing: '0.5px',
          }}
        >
          Contact Management
        </h1>
        <p className='text-lg font-work-sans' style={{ color: '#ede8c5' }}>
          Manage and view all contact submissions
        </p>
      </div>

      {/* Statistics Cards */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-8 mb-10'>
        <div
          className='rounded-lg p-8 border shadow-lg transition-all duration-300 hover:scale-105'
          style={{
            backgroundColor: '#0B222C',
            borderColor: '#095D52',
            boxShadow:
              '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
            backgroundImage:
              'linear-gradient(135deg, rgba(187, 228, 37, 0.05) 0%, rgba(9, 93, 82, 0.1) 50%, rgba(11, 34, 44, 0.95) 100%)',
          }}
        >
          <div className='flex items-center'>
            <div className='p-3 rounded-lg mr-4 transition-all duration-300 hover:scale-110'>
              <svg
                className='w-6 h-6'
                style={{ color: '#095D52' }}
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
              </svg>
            </div>
            <div>
              <p className='text-sm font-medium font-work-sans' style={{ color: '#ede8c5' }}>
                Total Contacts
              </p>
              <p className='text-2xl font-bold font-dm-serif' style={{ color: '#ede8c5' }}>
                {totalContacts}
              </p>
            </div>
          </div>
        </div>

        <div
          className='rounded-lg p-8 border shadow-lg transition-all duration-300 hover:scale-105'
          style={{
            backgroundColor: '#0B222C',
            borderColor: '#095D52',
            boxShadow:
              '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
            backgroundImage:
              'linear-gradient(135deg, rgba(187, 228, 37, 0.05) 0%, rgba(9, 93, 82, 0.1) 50%, rgba(11, 34, 44, 0.95) 100%)',
          }}
        >
          <div className='flex items-center'>
            <div className='p-3 rounded-lg mr-4 transition-all duration-300 hover:scale-110'>
              <svg
                className='w-6 h-6'
                style={{ color: '#095D52' }}
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path
                  fillRule='evenodd'
                  d='M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
            <div>
              <p className='text-sm font-medium font-work-sans' style={{ color: '#ede8c5' }}>
                Consented
              </p>
              <p className='text-2xl font-bold font-dm-serif' style={{ color: '#ede8c5' }}>
                {contacts.filter((contact) => contact.consent).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className='mb-8'>
        <div className='relative'>
          <input
            type='text'
            placeholder='Search contacts by name, email, country, or business type...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className='w-full px-4 py-3 pl-10 rounded-lg text-sm font-work-sans'
            style={{
              backgroundColor: '#0B222C',
              border: '1px solid #095D52',
              color: '#ede8c5',
              outline: 'none',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
            }}
          />
          <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
            <svg
              className='h-5 w-5'
              style={{ color: '#ede8c5' }}
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Contacts Table */}
      <div className='mb-10'>
        <div className='flex items-center justify-between mb-6'>
          <h2
            className='text-2xl font-bold font-dm-serif'
            style={{
              color: '#ede8c5',
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.5), 0 0 15px rgba(187, 228, 37, 0.3)',
              letterSpacing: '0.8px',
            }}
          >
            Contacts Directory
          </h2>
          <div className='flex items-center space-x-4'>
            <div className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
              {filteredContacts.length} of {totalContacts} contacts
            </div>
            <div className='w-2 h-2 rounded-full' style={{ backgroundColor: '#4ade80' }}></div>
          </div>
        </div>

        <div
          className='overflow-x-auto rounded-xl border-2 shadow-2xl transition-all duration-300'
          style={{
            backgroundColor: '#0B222C',
            borderColor: '#095D52',
            boxShadow:
              '0 20px 40px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
            backgroundImage:
              'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
          }}
        >
          <table className='w-full'>
            <thead
              style={{
                backgroundColor: '#095D52',
                backgroundImage:
                  'linear-gradient(135deg, rgba(187, 228, 37, 0.15) 0%, rgba(9, 93, 82, 0.9) 100%)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(187, 228, 37, 0.2)',
              }}
            >
              <tr>
                <th
                  className='px-6 py-4 text-left text-sm font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Contact Info
                </th>
                <th
                  className='px-6 py-4 text-left text-sm font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Location
                </th>
                <th
                  className='px-6 py-4 text-left text-sm font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Business
                </th>
                <th
                  className='px-6 py-4 text-left text-sm font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Status
                </th>
                <th
                  className='px-6 py-4 text-left text-sm font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Submitted
                </th>
                <th
                  className='px-6 py-4 text-center text-sm font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody
              style={{
                backgroundColor: '#0B222C',
                backgroundImage:
                  'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
              }}
              className='divide-y divide-gray-700'
            >
              {!Array.isArray(filteredContacts) || filteredContacts.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className='px-6 py-12 text-center font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    <div className='flex flex-col items-center'>
                      <div
                        className='w-16 h-16 rounded-full flex items-center justify-center mb-4'
                        style={{ backgroundColor: '#095D52' }}
                      >
                        <svg
                          className='w-8 h-8'
                          style={{ color: '#ede8c5' }}
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
                          />
                        </svg>
                      </div>
                      <p className='text-lg font-medium mb-2'>
                        {searchTerm
                          ? `No contacts found matching "${searchTerm}"`
                          : 'No contacts found'}
                      </p>
                      <p className='text-sm opacity-75'>
                        {searchTerm
                          ? 'Try adjusting your search criteria'
                          : 'Contacts will appear here once submitted'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredContacts.map((contact: Contact, index: number) => (
                  <tr
                    key={contact._id || index}
                    className='transition-all duration-200 hover:scale-[1.01]'
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#095D52';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(9, 93, 82, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <td className='px-6 py-4'>
                      <div className='flex items-center'>
                        <div
                          className='w-10 h-10 rounded-lg flex items-center justify-center mr-3'
                          style={{ backgroundColor: '#095D52' }}
                        >
                          <svg
                            className='w-5 h-5'
                            style={{ color: '#ede8c5' }}
                            fill='currentColor'
                            viewBox='0 0 20 20'
                          >
                            <path
                              fillRule='evenodd'
                              d='M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z'
                              clipRule='evenodd'
                            />
                          </svg>
                        </div>
                        <div>
                          <div
                            className='text-sm font-semibold font-work-sans'
                            style={{ color: '#ede8c5' }}
                          >
                            {contact.firstName} {contact.lastName}
                          </div>
                          <div
                            className='text-xs font-work-sans opacity-75'
                            style={{ color: '#ede8c5' }}
                          >
                            {contact.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className='px-6 py-4'>
                      <div className='flex items-center'>
                        <svg
                          className='w-4 h-4 mr-2'
                          style={{ color: '#095D52' }}
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'
                          />
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M15 11a3 3 0 11-6 0 3 3 0 016 0z'
                          />
                        </svg>
                        <span className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                          {contact.country}
                        </span>
                      </div>
                    </td>
                    <td className='px-6 py-4'>
                      <div className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                        {contact.businessType}
                      </div>
                    </td>
                    <td className='px-6 py-4'>
                      <span
                        className='inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium font-work-sans transition-all duration-200 hover:scale-105'
                        style={{
                          backgroundColor: contact.consent ? '#10B981' : '#EF4444',
                          color: '#ffffff',
                          boxShadow: contact.consent
                            ? '0 2px 6px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                            : '0 2px 6px rgba(239, 68, 68, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                          backgroundImage: contact.consent
                            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.8) 0%, rgba(5, 150, 105, 0.9) 100%)'
                            : 'linear-gradient(135deg, rgba(239, 68, 68, 0.8) 0%, rgba(220, 38, 38, 0.9) 100%)',
                        }}
                      >
                        <svg className='w-3 h-3 mr-1.5' fill='currentColor' viewBox='0 0 20 20'>
                          <path
                            fillRule='evenodd'
                            d={
                              contact.consent
                                ? 'M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                                : 'M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                            }
                            clipRule='evenodd'
                          />
                        </svg>
                        {getStatusText(contact.consent)}
                      </span>
                    </td>
                    <td className='px-6 py-4'>
                      <div className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                        {formatDate(contact.submittedAt)}
                      </div>
                    </td>
                    <td className='px-6 py-4 text-center'>
                      <button
                        onClick={() => fetchContactDetails(contact._id)}
                        disabled={contactLoading}
                        className='inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium font-work-sans transition-all duration-300 hover:scale-105'
                        style={{
                          backgroundColor: '#0B222C',
                          border: '2px solid #095D52',
                          color: '#ede8c5',
                          boxShadow:
                            '0 4px 12px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                          backgroundImage:
                            'linear-gradient(135deg, rgba(187, 228, 37, 0.05) 0%, rgba(9, 93, 82, 0.1) 50%, rgba(11, 34, 44, 0.95) 100%)',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#095D52';
                          e.currentTarget.style.borderColor = '#bbe425';
                          e.currentTarget.style.boxShadow =
                            '0 6px 20px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#0B222C';
                          e.currentTarget.style.borderColor = '#095D52';
                          e.currentTarget.style.boxShadow =
                            '0 4px 12px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)';
                        }}
                      >
                        <svg
                          className='w-4 h-4 mr-2'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                          />
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                          />
                        </svg>
                        {contactLoading ? 'Loading...' : 'View Details'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          className='flex items-center justify-between pt-6'
          style={{ borderTop: '2px solid #095D52' }}
        >
          <div className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
            Page {currentPage} of {totalPages} • Showing {filteredContacts.length} of{' '}
            {totalContacts} contacts
          </div>
          <div className='flex space-x-2'>
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className='px-4 py-2 rounded-lg text-sm font-medium font-work-sans transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed'
              style={{
                backgroundColor: currentPage === 1 ? '#095D5250' : '#095D52',
                color: '#ede8c5',
                border: '1px solid #095D52',
                boxShadow:
                  currentPage === 1
                    ? 'none'
                    : '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                backgroundImage:
                  currentPage === 1
                    ? 'none'
                    : 'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
              }}
              onMouseEnter={(e) => {
                if (currentPage !== 1) {
                  e.currentTarget.style.backgroundColor = '#bbe425';
                  e.currentTarget.style.boxShadow =
                    '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== 1) {
                  e.currentTarget.style.backgroundColor = '#095D52';
                  e.currentTarget.style.boxShadow =
                    '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                }
              }}
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className='px-4 py-2 rounded-lg text-sm font-medium font-work-sans transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed'
              style={{
                backgroundColor: currentPage === totalPages ? '#095D5250' : '#095D52',
                color: '#ede8c5',
                border: '1px solid #095D52',
                boxShadow:
                  currentPage === totalPages
                    ? 'none'
                    : '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                backgroundImage:
                  currentPage === totalPages
                    ? 'none'
                    : 'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
              }}
              onMouseEnter={(e) => {
                if (currentPage !== totalPages) {
                  e.currentTarget.style.backgroundColor = '#bbe425';
                  e.currentTarget.style.boxShadow =
                    '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== totalPages) {
                  e.currentTarget.style.backgroundColor = '#095D52';
                  e.currentTarget.style.boxShadow =
                    '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                }
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Contact Details Modal */}
      {showContactModal && selectedContact && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div
            className='rounded-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-lg'
            style={{
              backgroundColor: '#0B222C',
              border: '2px solid #095D52',
              boxShadow:
                '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
              backgroundImage:
                'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
            }}
          >
            <div
              className='flex items-center justify-between p-4'
              style={{ borderBottom: '2px solid #095D52' }}
            >
              <h3
                className='text-lg font-semibold font-dm-serif'
                style={{
                  color: '#ede8c5',
                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.5), 0 0 15px rgba(187, 228, 37, 0.3)',
                  letterSpacing: '0.8px',
                }}
              >
                Contact Details
              </h3>
              <button
                onClick={() => setShowContactModal(false)}
                className='text-2xl transition-all duration-300 hover:scale-110'
                style={{ color: '#ede8c5' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#bbe425';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#ede8c5';
                }}
              >
                <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>

            <div className='p-4'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                {/* Basic Information */}
                <div
                  className='rounded-lg p-3'
                  style={{
                    backgroundColor: '#0B222C',
                    border: '1px solid #095D52',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                  }}
                >
                  <h4
                    className='text-md font-semibold mb-4 font-dm-serif'
                    style={{ color: '#ede8c5' }}
                  >
                    Basic Information
                  </h4>
                  <div className='space-y-3'>
                    <div>
                      <label
                        className='text-sm font-medium font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        Full Name
                      </label>
                      <p className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                        {selectedContact.firstName} {selectedContact.lastName}
                      </p>
                    </div>
                    <div>
                      <label
                        className='text-sm font-medium font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        Email
                      </label>
                      <p className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                        {selectedContact.email}
                      </p>
                    </div>
                    <div>
                      <label
                        className='text-sm font-medium font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        Confirm Email
                      </label>
                      <p className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                        {selectedContact.confirmEmail}
                      </p>
                    </div>
                    <div>
                      <label
                        className='text-sm font-medium font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        Country
                      </label>
                      <p className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                        {selectedContact.country}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Business Information */}
                <div
                  className='rounded-lg p-3'
                  style={{
                    backgroundColor: '#0B222C',
                    border: '1px solid #095D52',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                  }}
                >
                  <h4
                    className='text-md font-semibold mb-4 font-dm-serif'
                    style={{ color: '#ede8c5' }}
                  >
                    Business Information
                  </h4>
                  <div className='space-y-3'>
                    <div>
                      <label
                        className='text-sm font-medium font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        Business Type
                      </label>
                      <p className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                        {selectedContact.businessType}
                      </p>
                    </div>
                    <div>
                      <label
                        className='text-sm font-medium font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        Consent Status
                      </label>
                      <p className='text-sm'>
                        <span
                          className='inline-flex items-center px-3 py-1 rounded-full text-xs font-medium font-work-sans'
                          style={{
                            backgroundColor: selectedContact.consent ? '#10B981' : '#EF4444',
                            color: '#ede8c5',
                          }}
                        >
                          {selectedContact.consent ? 'Consented' : 'Not Consented'}
                        </span>
                      </p>
                    </div>
                    <div>
                      <label
                        className='text-sm font-medium font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        Submitted At
                      </label>
                      <p className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                        {formatDate(selectedContact.submittedAt)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comments */}
              <div className='mt-6'>
                <h4
                  className='text-md font-semibold mb-4 font-dm-serif'
                  style={{ color: '#ede8c5' }}
                >
                  Comments
                </h4>
                <div
                  className='p-4 rounded-lg'
                  style={{
                    backgroundColor: '#0B222C',
                    border: '1px solid #095D52',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                  }}
                >
                  <p className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                    {selectedContact.comments}
                  </p>
                </div>
              </div>

              {/* Modal Actions */}
              <div
                className='flex justify-end mt-6 pt-4'
                style={{ borderTop: '2px solid #095D52' }}
              >
                <button
                  onClick={() => setShowContactModal(false)}
                  className='px-6 py-2 rounded-lg text-sm font-medium font-work-sans transition-all duration-300 hover:scale-105'
                  style={{
                    backgroundColor: '#095D52',
                    border: '1px solid #095D52',
                    color: '#ede8c5',
                    boxShadow: '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                    backgroundImage:
                      'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#bbe425';
                    e.currentTarget.style.boxShadow =
                      '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#095D52';
                    e.currentTarget.style.boxShadow =
                      '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsPage;
